import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { NavigationItem, isNavigationGroup } from '../types'

const STORAGE_KEY = 'nav-expanded-groups'

export function useNavigationState(items: NavigationItem[]) {
  const location = useLocation()

  // Initialize from localStorage or defaults
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return new Set(parsed)
      }
    } catch {
      // Ignore parse errors
    }

    // Default: expand all groups with defaultOpen=true
    const defaultOpen = new Set<string>()
    for (const item of items) {
      if (isNavigationGroup(item) && item.defaultOpen) {
        defaultOpen.add(item.id)
      }
    }
    return defaultOpen
  })

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...expandedGroups]))
  }, [expandedGroups])

  // Auto-expand group containing active route
  useEffect(() => {
    items.forEach((item) => {
      if (isNavigationGroup(item)) {
        const hasActiveChild = item.children.some((child) =>
          location.pathname.startsWith(child.path)
        )
        if (hasActiveChild && !expandedGroups.has(item.id)) {
          setExpandedGroups((prev) => new Set([...prev, item.id]))
        }
      }
    })
  }, [location.pathname, items])

  // Toggle group open/closed (multiple can be open)
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  const isGroupExpanded = useCallback(
    (groupId: string) => {
      return expandedGroups.has(groupId)
    },
    [expandedGroups]
  )

  const expandAll = useCallback(() => {
    const allGroupIds = items.filter(isNavigationGroup).map((g) => g.id)
    setExpandedGroups(new Set(allGroupIds))
  }, [items])

  const collapseAll = useCallback(() => {
    setExpandedGroups(new Set())
  }, [])

  return {
    expandedGroups,
    toggleGroup,
    isGroupExpanded,
    expandAll,
    collapseAll
  }
}
